import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { useI18n } from '../../lib/i18n';
import { SearchIcon, CloseIcon } from '../icons';

export interface FilterBarProps {
  search?: {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
  };
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function FilterBar({ search, children, actions, className }: FilterBarProps) {
  const { t } = useI18n();
  // This one component fronts the search box on Subscribers, Broadcasting,
  // Templates, … — so its accessible name and default placeholder were
  // hardcoded English on every one of those pages.
  const placeholder = search?.placeholder || t('common.search');
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-b border-line bg-slate-50/50 p-4',
        'dark:border-slate-800 dark:bg-slate-800/30',
        className,
      )}
    >
      <div className="flex flex-1 flex-wrap items-center gap-2.5">
        {search && (
          <div className="relative min-w-[200px] max-w-xs flex-1">
            <SearchIcon
              size={15}
              className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-ink-3 dark:text-slate-400"
            />
            <input
              type="text"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={placeholder}
              aria-label={placeholder}
              className={cn(
                'w-full rounded-lg border border-slate-200 bg-white py-1.5 ps-9 pe-8 text-[13px] text-ink',
                'placeholder:text-ink-4 outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand',
                'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500',
              )}
            />
            {search.value && (
              <button
                type="button"
                onClick={() => search.onChange('')}
                aria-label={t('common.clearSearch')}
                className="absolute end-2.5 top-1/2 -translate-y-1/2 text-ink-4 hover:text-ink dark:text-slate-500 dark:hover:text-slate-300"
              >
                <CloseIcon size={13} />
              </button>
            )}
          </div>
        )}

        {children}
      </div>

      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export default FilterBar;
